import {
  TradeMessageSubType,
  TradeMessageType,
} from "@/lib/interfaces and Enums/ENUMBondTradeMessage";
import {
  ISwaptionLegDetails,
  ISwaptionSecurityDetails,
  ISwaptionTradeMessage,
  SwaptionDirection,
  TradeOptionType,
} from "@/lib/interfaces and Enums/ENUMOptionTradeMessage";
import { TradeDirection } from "@/lib/interfaces and Enums/ENUMTradeDirection";
import { IPortfolio } from "@/lib/interfaces and Enums/ENUMPortfolio";
import { returnSizeFormatted } from "@/lib/globalFormatterHelper";
import { TradeInterestType } from "@/lib/interfaces and Enums/ENUMSwapTradeMessage";
import { ISwapLegTicket, ISwaptionTicket } from "@/store/tradeFromScratchStore";
import {
  ISwapTicketFieldEntry,
  SwapLegKey,
  isFixedLeg,
  returnMissingLegEntries,
  returnRateAsWireDecimal,
  returnCpiIssues,
  returnEffectiveFirstCouponFrom,
  returnZcsCpiDetails,
  returnRequiredLegKeys,
  returnWireResetFrequency,
} from "./swapTicketHelper";
import { returnAllocationIssues, returnWireAllocations } from "./allocationRules";
import {
  ITicketField,
  returnMissingTicketEntries,
  returnRequiredTicketKeys,
  returnTransactionNumberDetail,
} from "./ticketSharedHelper";

// LQC-2677 - pure rules for the swaption ticket. Composes the swap leg rules with the
// option terms, so the leg helpers are imported from swapTicketHelper rather than copied.
// Read off LQ.Application/Trades/Swaption/Incoming*.cs.

// isDate, not a truthiness check - a five digit year from the date input is truthy and
// unsendable, and is exactly what broke POST /Trade/Swaption (LQC-2677).
export const SWAPTION_TICKET_REQUIRED_TRADE_FIELDS: ITicketField<ISwaptionTicket>[] = [
  { key: "portfolioCode", label: "Portfolio" },
  { key: "nominal", label: "Nominal", isNumeric: true },
  { key: "direction", label: "Direction" },
  { key: "expiryDate", label: "Expiry Date", isDate: true },
  { key: "currencyPayment", label: "Payment Currency" },
  { key: "startDate", label: "Start Date", isDate: true },
  { key: "maturityDate", label: "Maturity", isDate: true },
  { key: "settlementDate", label: "Settlement Date", isDate: true },
  { key: "tradeDate", label: "Trade Date", isDate: true },
];

// LQC-2959 - the API still wants optionType, so it is read off the one Direction choice.
export function returnSwaptionWireDirection(direction: SwaptionDirection): {
  direction: TradeDirection;
  optionType: TradeOptionType;
} {
  return {
    direction:
      direction === SwaptionDirection.BuyPayer ||
      direction === SwaptionDirection.BuyReceiver
        ? TradeDirection.Buy
        : TradeDirection.Sell,
    optionType:
      direction === SwaptionDirection.BuyPayer ||
      direction === SwaptionDirection.SellPayer
        ? TradeOptionType.Payer
        : TradeOptionType.Receiver,
  };
}

// Spec F10 (corrected 2 Oct): both words decide it. Buy Payer and Sell Receiver pay fixed,
// Sell Payer and Buy Receiver receive it. Display only: the API has no leg orientation.
export function returnSwaptionLegOrientation(
  ticket: Pick<ISwaptionTicket, "direction" | "leg1" | "leg2">,
  legKey: SwapLegKey,
): string {
  if (!ticket.direction) return "";
  const leg = ticket[legKey];
  const otherLeg = legKey === "leg1" ? ticket.leg2 : ticket.leg1;
  // Two fixed or two floating legs have no Pay Fixed side to name.
  if (leg.interestType === otherLeg.interestType) return "";
  const paysFixed =
    ticket.direction === SwaptionDirection.BuyPayer ||
    ticket.direction === SwaptionDirection.SellReceiver;
  const isFixed = leg.interestType === TradeInterestType.Fixed;
  return `${isFixed === paysFixed ? "Pay" : "Receive"} ${isFixed ? "Fixed" : "Floating"}`;
}

export function returnMissingSwaptionTicketEntries(
  ticket: ISwaptionTicket,
): ISwapTicketFieldEntry[] {
  return [
    ...returnMissingTicketEntries(
      ticket,
      SWAPTION_TICKET_REQUIRED_TRADE_FIELDS,
    ).map(({ key, label }) => ({ key: String(key), label })),
    ...returnMissingLegEntries(ticket.leg1, "leg1", "Leg 1", true),
    ...returnMissingLegEntries(ticket.leg2, "leg2", "Leg 2", true),
    ...returnCpiIssues(ticket),
    ...returnAllocationIssues(ticket.nominal, ticket.additionalAllocations),
  ];
}

export function returnMissingSwaptionTicketFields(
  ticket: ISwaptionTicket,
): string[] {
  return returnMissingSwaptionTicketEntries(ticket).map((entry) => entry.label);
}

export function isSwaptionTicketComplete(ticket: ISwaptionTicket): boolean {
  return returnMissingSwaptionTicketEntries(ticket).length === 0;
}

// Every key the ticket can mark required, leg fields included. Leg keys depend on the
// leg's type, so this is a function of the ticket rather than a constant.
export function returnRequiredSwaptionTicketKeys(
  ticket: ISwaptionTicket,
): Set<string> {
  return new Set([
    ...returnRequiredTicketKeys(SWAPTION_TICKET_REQUIRED_TRADE_FIELDS),
    ...returnRequiredLegKeys(ticket.leg1, "leg1", true),
    ...returnRequiredLegKeys(ticket.leg2, "leg2", true),
  ]);
}

// ValidateExpiryDateWithSettlementDate - the option cannot expire before it settles.
export function isExpiryAfterSettlement(ticket: ISwaptionTicket): boolean {
  if (!ticket.expiryDate || !ticket.settlementDate) return true;
  return ticket.expiryDate >= ticket.settlementDate;
}

// ValidateMaturityDateWithSettlementDate, on the UNDERLYING swap.
export function isUnderlyingMaturityAfterSettlement(
  ticket: ISwaptionTicket,
): boolean {
  if (!ticket.maturityDate || !ticket.settlementDate) return true;
  return ticket.maturityDate >= ticket.settlementDate;
}

// Names the field as well as the problem, so the ticket can put the message on the row
// that is wrong instead of in a footer the user has to scroll to find.
export interface ISwaptionDateIssue {
  key: "expiryDate" | "maturityDate";
  message: string;
}

export function returnSwaptionDateIssue(
  ticket: ISwaptionTicket,
): ISwaptionDateIssue | null {
  if (!isExpiryAfterSettlement(ticket)) {
    return {
      key: "expiryDate",
      message: `Must be on or after settlement (${ticket.settlementDate})`,
    };
  }
  if (!isUnderlyingMaturityAfterSettlement(ticket)) {
    return {
      key: "maturityDate",
      message: `Must be on or after settlement (${ticket.settlementDate})`,
    };
  }
  return null;
}

export function returnSwaptionDateError(ticket: ISwaptionTicket): string {
  return returnSwaptionDateIssue(ticket)?.message ?? "";
}

export function returnSentSwaptionTicketSummary(ticket: ISwaptionTicket): string {
  const nominal =
    typeof ticket.nominal === "number" && ticket.nominal > 0
      ? returnSizeFormatted(ticket.nominal)
      : "";
  const strike =
    typeof ticket.strike === "number" && ticket.strike > 0
      ? `@ ${ticket.strike}`
      : "";
  return [ticket.direction, nominal, ticket.leg1.currency, strike]
    .filter(Boolean)
    .join(" ");
}

// No spread: the swaption leg model has no such property, so it is dropped rather than
// sent and ignored.
function returnLegDetails(
  leg: ISwapLegTicket,
  isCrossCurrency: boolean,
): ISwaptionLegDetails {
  const details: ISwaptionLegDetails = {
    interestType: leg.interestType,
    couponFrequency: leg.couponFrequency,
    currency: leg.currency,
    currencyPayment: leg.currency,
    dayCount: leg.dayCount,
    businessDay: leg.businessDay,
    calendar: leg.calendar,
  };
  if (typeof leg.couponRate === "number") {
    details.couponRate = returnRateAsWireDecimal(leg.couponRate);
  }
  const resetFrequency = returnWireResetFrequency(leg, isCrossCurrency);
  if (resetFrequency) details.resetFrequency = resetFrequency;
  if (isFixedLeg(leg)) return details;
  details.referenceRate = leg.referenceRate;
  return details;
}

function returnSecurityDetails(
  ticket: ISwaptionTicket,
  optionType: TradeOptionType,
): ISwaptionSecurityDetails {
  const details: ISwaptionSecurityDetails = {
    optionType,
    exerciseType: ticket.exerciseType,
    premiumType: ticket.premiumType,
    delivery: ticket.delivery,
    expiryDate: ticket.expiryDate,
    cleared: ticket.cleared,
    currencyPayment: ticket.currencyPayment,
    calendar: ticket.leg1.calendar,
  };
  // The API Range check rejects 0, so an unset strike is omitted rather than sent.
  if (typeof ticket.strike === "number" && ticket.strike > 0) {
    details.strike = ticket.strike;
    details.strikeQuotation = ticket.strikeQuotation;
  }
  return details;
}

export interface IBuildSwaptionTradeMessageParams {
  ticket: ISwaptionTicket;
  portfolio: IPortfolio | undefined;
  traderName: string;
  // Used only when the portfolio row carries no customerCode.
  fallbackEntity?: string;
}

export function buildSwaptionTradeMessage({
  ticket,
  portfolio,
  traderName,
  fallbackEntity = "",
}: IBuildSwaptionTradeMessageParams): ISwaptionTradeMessage | undefined {
  if (!isSwaptionTicketComplete(ticket)) return undefined;
  if (!portfolio) return undefined;
  if (returnSwaptionDateError(ticket)) return undefined;
  const nominal = ticket.nominal as number;
  const isCrossCurrency = ticket.leg1.currency !== ticket.leg2.currency;
  const wire = returnSwaptionWireDirection(ticket.direction as SwaptionDirection);

  return {
    messageType: TradeMessageType.Trade,
    subType: TradeMessageSubType.Swaption,
    clientDetails: {
      originatingEntity: portfolio.customerCode || fallbackEntity,
      lei: portfolio.lei,
      traderName,
    },
    // Empty - the swaption security does not exist yet and the back end builds it.
    security: {},
    securityDetails: returnSecurityDetails(ticket, wire.optionType),
    underlyingSecurityDetails: {
      startDate: ticket.startDate,
      maturityDate: ticket.maturityDate,
      firstCouponFrom: returnEffectiveFirstCouponFrom(ticket),
      // Separate from the option's own cleared flag.
      cleared: ticket.underlyingCleared,
      ...returnZcsCpiDetails(ticket),
      securityDetailsLeg1: returnLegDetails(ticket.leg1, isCrossCurrency),
      securityDetailsLeg2: returnLegDetails(ticket.leg2, isCrossCurrency),
    },
    tradeDetails: {
      tradeDate: ticket.tradeDate,
      direction: wire.direction,
      nominal,
      settlementDate: ticket.settlementDate,
      // ValidateOpenClose rejects null AND Substitute, so this always travels.
      openClose: ticket.openClose,
      ...returnTransactionNumberDetail(ticket.transactionNumber),
    },
    // No Leg 2 capture on a swaption yet, but a missing nominal2 is stored as 0 (LQC-2973).
    allocations: returnWireAllocations(
      portfolio.portfolioCode,
      nominal,
      ticket.additionalAllocations,
      (allocationNominal) => allocationNominal,
    ),
  };
}
